import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
  API_URL,
  COOKIE_DOMAIN,
  I18N_ROUTING_ENABLED,
  LANG_COOKIE_NAME,
  NAMESPACES,
  SITE_DOMAIN,
  SUPPORTED_LNGS,
  type AppLocale,
  type Namespace,
} from './locales';

// Re-exports — preserva imports legados (`from '../i18n'`) sem alterações.
export { API_URL, COOKIE_DOMAIN, I18N_ROUTING_ENABLED, LANG_COOKIE_NAME, NAMESPACES, SITE_DOMAIN, SUPPORTED_LNGS };
export type { AppLocale, Namespace };

/**
 * i18n da web (Release C). PT-BR default e fallback absoluto.
 *
 * As traduções vêm do backend (`GET /api/i18n/{lng}/{ns}`), que serve a
 * tabela `translations` (editável pelo admin). Se a chamada falhar, o
 * i18next mantém o que já tiver em cache; chave ausente → fallback pt-BR;
 * pt-BR ausente → a própria string default passada em `t(key, 'default')`.
 * Logo, nada quebra mesmo com backend i18n fora do ar (retrocompat).
 *
 * Constantes/tipos vivem em ./locales.ts (Node-safe). Só este arquivo
 * dispara o `i18n.init()`, que tem deps de browser — scripts `tsx` devem
 * importar de ./locales (ou de slugMap, que também só importa locales).
 */

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    backend: {
      loadPath: `${API_URL}/i18n/{{lng}}/{{ns}}`,
    },
    fallbackLng: 'pt-BR',
    supportedLngs: SUPPORTED_LNGS as unknown as string[],
    // NÃO usar nonExplicitSupportedLngs: ele reduz o código à parte do idioma
    // ('pt-BR' -> 'pt') antes de checar a lista; como supportedLngs tem
    // 'pt-BR' (com região) e não 'pt', o português nunca casava e a detecção
    // caía sempre em 'en'. Sem a flag: 'pt-BR' casa exato; 'en-US'/'es-ES'
    // resolvem para 'en'/'es' pelo fallback de locale similar do i18next.
    load: 'currentOnly',
    ns: ['common'],
    defaultNS: 'common',
    // O backend serve o bundle FLAT — `{ "nav.home": "Início" }` — com a
    // chave literal contendo pontos. keySeparator:false faz o i18next tratar
    // 'nav.home' como chave única (sem navegar objeto aninhado).
    keySeparator: false,
    detection: {
      // Ordem: URL prefix (/en, /es) → query ?lang= (usado pelos iframes
      // embed colados em sites de terceiros) → cookie (compartilhado entre
      // subdomínios) → localStorage (fallback per-origin) → navegador → htmlTag.
      // PT-BR sem prefixo = default.
      order: ['path', 'querystring', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      lookupFromPathIndex: 0,
      lookupQuerystring: 'lang',
      lookupLocalStorage: LANG_COOKIE_NAME,
      lookupCookie: LANG_COOKIE_NAME,
      // COOKIE_DOMAIN='bolaoelite.com.br' (CI/CD) → escopo '.bolaoelite.com.br'
      // cobre PROD root, app.*, DEV root, DEV app.*. DEV usa cookie com nome
      // 'bolao-lang-dev' (LANG_COOKIE_NAME) pra não conflitar com PROD que
      // compartilha o mesmo domain. Em dev local sem COOKIE_DOMAIN, cookie
      // cai no host atual e LANG_COOKIE_NAME default = 'bolao-lang'.
      cookieDomain: COOKIE_DOMAIN ? `.${COOKIE_DOMAIN}` : undefined,
      cookieMinutes: 60 * 24 * 365, // 1 ano
      // O detector escreve no cookie e no localStorage sempre que o idioma mudar.
      caches: ['cookie', 'localStorage'],
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export default i18n;
